'use client';

import { FC, useState } from 'react';

import { ElasticUtils } from '@ravago/shared/next-data-access/client';
import { AutoSuggestQueryArgs, AutoSuggestResponse, FacetFilterWithTerms } from '@ravago/shared/page-data/models/elastic';
import { DynamicPageUrl, RendererGlobals } from '@ravago/shared/page-data/models/utils';

import { useQuery } from '@tanstack/react-query';

import { useRouter } from 'next/navigation';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { AutoSuggest } from '../../../shared/auto-suggest/auto-suggest';
import { AutoSuggestClientRequest } from '../../product-portfolio/models/client-request.model';
import { filterToQuery } from '../../product-portfolio/utils/filter-query-utils';

interface Props {
   baseRoutes?: {
      product: string;
      brand: string;
      caseStudy: string;
   };
   productOverviewRoute: DynamicPageUrl | null;
   locale: string;
   globals: RendererGlobals;
}

const AUTO_SUGGEST_SIZE = 70;

const getAutoSuggestResult = async ({ search, correlationId }: Omit<AutoSuggestClientRequest, 'facets'>): Promise<AutoSuggestResponse> => {
   const request: AutoSuggestQueryArgs = {
      autoSuggestRequest: {
         query: search.trim()
      },
      size: AUTO_SUGGEST_SIZE
   };

   return ElasticUtils.getAutosuggestResult(request, correlationId);
};

export const HomepageSearch: FC<Props> = ({ baseRoutes, productOverviewRoute, locale, globals }) => {
   const router = useRouter();
   const [search, setSearch] = useState('');
   const [autoSuggestExpanded, setAutoSuggestExpanded] = useState(false);

   const language = getLanguageCode(locale);

   const autoSuggestQuery = useQuery({
      queryKey: ['auto-suggest', { search }],
      queryFn: () => getAutoSuggestResult({ search, correlationId: CorrelationService.generateCorrelationId('dpcid') }),
      enabled: () => search.length > 1 && autoSuggestExpanded
   });

   const handleSearch = (value?: string, facetFilters?: Array<FacetFilterWithTerms>) => {
      if (!productOverviewRoute?.url) return;

      const params = new URLSearchParams();

      params.set('tab', 'products');

      if (value) params.set('search', value);

      if (facetFilters?.length) params.set('filter', filterToQuery(facetFilters));

      router.push(`/${locale}/${productOverviewRoute.url}?${params.toString()}`);
   };

   return (
      <AutoSuggest
         id="global-search"
         language={language}
         result={autoSuggestQuery.data}
         loading={autoSuggestQuery.isFetching}
         baseRoutes={baseRoutes}
         globals={globals}
         onChange={setSearch}
         onSearch={handleSearch}
         onFacetsClick={(facets) => handleSearch(undefined, facets)}
         productOverviewRoute={productOverviewRoute}
         expanded={autoSuggestExpanded}
         onAutoSuggestMenuChange={(value) => setAutoSuggestExpanded(value)}
         facetType="apply"
      />
   );
};
